import { z } from 'zod';
/** Things people are told about. */
export const notificationEvents = [
    'incoming-call',
    'missed-call',
    'voicemail',
    'text',
    'queue-callback',
    'queue-waiting',
    'weekly-digest',
    'call-blocked',
    'trunk-down',
    'ai-budget-reached',
];
/** Where: inside the app (a list with unread counts), a push to the browser on a computer, or a push to the phone app. */
export const notificationChannels = ['inApp', 'push', 'phone'];
/** Who each event is for, and which channels it can use. */
export const notificationEventInfo = {
    // An incoming call rings in the app already; the push is for when it's closed.
    'incoming-call': { adminsOnly: false, channels: ['push', 'phone'] },
    'missed-call': { adminsOnly: false, channels: ['inApp', 'push', 'phone'] },
    voicemail: { adminsOnly: false, channels: ['inApp', 'push', 'phone'] },
    // A text to a number whose texts go to the person (or their queue).
    text: { adminsOnly: false, channels: ['inApp', 'push', 'phone'] },
    // A queue call nobody answered (or a caller who asked to be called back): the queue's members.
    'queue-callback': { adminsOnly: false, channels: ['inApp', 'push', 'phone'] },
    // A caller has waited long in a queue: its managers (admins and the region's managers).
    'queue-waiting': { adminsOnly: false, channels: ['inApp', 'push', 'phone'] },
    'weekly-digest': { adminsOnly: false, channels: ['inApp', 'push', 'phone'] },
    'call-blocked': { adminsOnly: true, channels: ['inApp', 'push', 'phone'] },
    'trunk-down': { adminsOnly: true, channels: ['inApp', 'push', 'phone'] },
    'ai-budget-reached': { adminsOnly: true, channels: ['inApp', 'push', 'phone'] },
};
/** What everyone gets unless the tenant or the person chooses otherwise. */
export const DEFAULT_NOTIFICATION_PREFERENCES = {
    'incoming-call': { inApp: false, push: true, phone: true },
    'missed-call': { inApp: true, push: true, phone: true },
    voicemail: { inApp: true, push: true, phone: true },
    text: { inApp: true, push: true, phone: true },
    'queue-callback': { inApp: true, push: false, phone: false },
    'queue-waiting': { inApp: true, push: true, phone: false },
    'weekly-digest': { inApp: true, push: false, phone: false },
    'call-blocked': { inApp: true, push: false, phone: false },
    'trunk-down': { inApp: true, push: true, phone: true },
    'ai-budget-reached': { inApp: true, push: true, phone: false },
};
/** Our defaults, then the tenant's, then the person's own choices. */
export function effectivePreferences(...layers) {
    const result = structuredClone(DEFAULT_NOTIFICATION_PREFERENCES);
    for (const layer of layers) {
        for (const event of notificationEvents) {
            for (const channel of notificationEventInfo[event].channels) {
                const value = layer?.[event]?.[channel];
                if (typeof value === 'boolean')
                    result[event][channel] = value;
            }
        }
    }
    return result;
}
const channelChoices = z.object({ inApp: z.boolean().optional(), push: z.boolean().optional(), phone: z.boolean().optional() }).strict();
export const notificationPreferencesSchema = z.partialRecord(z.enum(notificationEvents), channelChoices);
export const notificationSettingsSchema = z.object({
    /** The tenant's defaults. */
    defaults: notificationPreferencesSchema,
    /** Whether push notifications (seen on a locked screen) name the caller. */
    pushShowsCaller: z.boolean(),
});
/** A browser's Web Push subscription, as PushSubscription.toJSON() gives it. */
export const pushSubscriptionSchema = z.object({
    endpoint: z.url({ protocol: /^https?$/ }).max(2000),
    expirationTime: z.number().nullable().optional(),
    keys: z.object({
        p256dh: z.string().regex(/^[A-Za-z0-9_-]{40,200}$/),
        auth: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
    }),
});
/**
 * A phone app's push token: from Apple (APNs, iPhone) or Google (FCM,
 * Android). "sandbox" is Apple's development service (builds from Xcode);
 * "test" is the fake push service of the automated tests, accepted only where
 * one is configured.
 */
export const phonePushTokenSchema = z.object({
    platform: z.enum(['apns', 'fcm']),
    token: z.string().regex(/^[A-Za-z0-9_:.-]{20,4096}$/),
    environment: z.enum(['production', 'sandbox', 'test']).default('production'),
    /** iPhones: the PushKit token, for incoming calls (Android phones get calls on their one token). */
    voipToken: z
        .string()
        .regex(/^[0-9a-fA-F]{64,200}$/)
        .optional(),
});
